import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTelegram } from "../../hooks/useTelegram";
import { useNavigate } from "react-router-dom";
import {
	updateFavorite,
	getRatesByGameIdPaged,
} from "../../router/apiServiceFetch";
import { useAuth } from "../../contexts/AuthProvider";
import { useItemContext } from "../../contexts/ItemContext";
import { CartIcon, HeartIcon } from "../GetIcons";
import Back from "../../assets/back.svg";
import starY from "../../assets/starY.png";
import starW from "../../assets/starW.png";
import AlertIcon from "../../assets/alert.svg";
import "./Item.scss";

const Item = ({ gameItem, onClose }) => {
	const navigate = useNavigate();
	const [allRates, setAllRates] = useState([]);
	const { handleAddToCart, handleRemoveFromCart, cartItems } = useItemContext();
	const { triggerHapticFeedback, tg } = useTelegram();
	const { userBase } = useAuth();

	const [isVisible, setIsVisible] = useState(false);
	const [itemData, setItemData] = useState(() => ({
		...gameItem,
	}));

	const filePath = process.env.REACT_APP_FILE_PATH;
	const [reviewsPage, setReviewsPage] = useState(1);
	const [totalReviewsPages, setTotalReviewsPages] = useState(1);
	const [reviewsLoading, setReviewsLoading] = useState(false);
	const loaderRef = useRef(null);

	const formatDate = (dateString) => {
		if (!dateString) return "";

		const armenianDate = new Date(dateString);
		const moscowOffset = -1 * 60 * 60 * 1000;
		const moscowDate = new Date(armenianDate.getTime() + moscowOffset);

		return `${moscowDate.getFullYear()}/${String(
			moscowDate.getMonth() + 1,
		).padStart(
			2,
			"0",
		)}/${String(moscowDate.getDate()).padStart(2, "0")} ${String(
			moscowDate.getHours(),
		).padStart(2, "0")}:${String(moscowDate.getMinutes()).padStart(2, "0")}`;
	};

	const fetchRates = async (page = 1, append = false) => {
		if (!gameItem?.gameId || reviewsLoading) return;

		try {
			setReviewsLoading(true);

			const result = await getRatesByGameIdPaged(gameItem.gameId, page, 50);
			const items = result.items || [];

			setAllRates((prev) => (append ? [...prev, ...items] : items));
			setReviewsPage(result.page ?? page);
			setTotalReviewsPages(result.totalPages ?? 1);
		} catch (error) {
			console.error("Error fetching rates:", error);
			if (!append) {
				setAllRates([]);
				setTotalReviewsPages(1);
			}
		} finally {
			setReviewsLoading(false);
		}
	};

	useEffect(() => {
		setAllRates([]);
		setReviewsPage(1);
		setTotalReviewsPages(1);
		fetchRates(1, false);

		const timeout = setTimeout(() => {
			setIsVisible(true);
		}, 10);

		return () => clearTimeout(timeout);
	}, [gameItem?.gameId]);

	const loadNextReviews = useCallback(() => {
		if (reviewsLoading) return;
		if (reviewsPage >= totalReviewsPages) return;

		fetchRates(reviewsPage + 1, true);
	}, [reviewsLoading, reviewsPage, totalReviewsPages, gameItem?.gameId]);

	useEffect(() => {
		if (!loaderRef.current) return;
		if (reviewsPage >= totalReviewsPages) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const first = entries[0];
				if (first.isIntersecting) {
					loadNextReviews();
				}
			},
			{
				root: null,
				rootMargin: "120px",
				threshold: 0.1,
			},
		);

		observer.observe(loaderRef.current);

		return () => observer.disconnect();
	}, [loadNextReviews, reviewsPage, totalReviewsPages]);

	const formatCurrency = (number) => {
		return (
			new Intl.NumberFormat("ru-RU", {
				currency: "RUB",
				minimumFractionDigits: 2,
			}).format(number) + " руб"
		);
	};

	const handleClose = () => {
		triggerHapticFeedback("impact", "medium");
		setIsVisible(false);
		setTimeout(onClose, 300);
	};

	const handleAdd = (item) => {
		handleAddToCart(item);
	};

	const handleRemove = (item) => {
		handleRemoveFromCart(item);
	};

	const handleFav = async (item) => {
		setItemData((prev) => ({
			...prev,
			isFav: !prev.isFav,
		}));

		try {
			await updateFavorite(userBase.userId, item.itemId);
		} catch (error) {
			console.error("Error updating favorite (item):", error);
		}
	};

	const cartItemInCart = cartItems.find((c) => c.itemId === gameItem.itemId);

	return (
		<div className={`item-cont ${isVisible ? "visible" : ""}`}>
			<div className="allGameItems">
				<div className="nav">
					<div className="back-nav">
						<div className="back-btn" onClick={handleClose}>
							<button>
								<img src={Back} alt="back" />
							</button>
						</div>
					</div>
				</div>

				<div className="item-content">
					<div className="alert">
						<img src={AlertIcon} alt="item" />
						<p>Перед покупкой проверьте наличие товара у вас на аккаунте</p>
					</div>

					<div className="item-content-inner-img">
						<img
							src={`${filePath}/Games/${itemData.fileName || ""}`}
							alt="item"
						/>

						<div
							className={`heartIcon ${itemData.isFav ? "isActiveFave" : ""}`}
							onClick={() => handleFav(itemData)}
						>
							<HeartIcon
								fillColor={itemData.isFav ? "#4A0073" : "#fff"}
								stroke={itemData.isFav ? "#8E00DD " : "none"}
							/>
						</div>

						<div className="item-content-inner-info-texts">
							<h2>{gameItem.name}</h2>
							<p className="item-content-inner-info-texts-price">
								<p>{formatCurrency(gameItem.priceAfterDiscount)}</p>
								{gameItem.discount > 0 && (
									<span className="disc">-{`${gameItem.discount}%`}</span>
								)}
							</p>
						</div>

						<div className="grad"></div>
					</div>

					<div className="border-div">
						<div className="border-div"></div>
					</div>

					<div className="description">
						<p>{gameItem.description}</p>
					</div>

					{cartItemInCart ? (
						<div className="gameSliderBtnDiv">
							<div className="gameSliderButtons">
								<button onClick={() => handleRemove(itemData)}>-</button>
							</div>
							<p className="quantity">{cartItemInCart.quantity}</p>
							<div className="gameSliderButtons">
								<button onClick={() => handleAdd(itemData)}>+</button>
							</div>
						</div>
					) : (
						<div className="gameSliderBtn">
							<button onClick={() => handleAdd(itemData)}>
								В Корзину
								<CartIcon fillColor="#FFAA00" />
							</button>
						</div>
					)}

					<div className="item-content-reactions">
						<div className="box-nav-border">
							<div className="border" />
							<h2>Отзывы пользователей</h2>
						</div>

						{allRates.length > 0 ? (
							<>
								{allRates.map((rate, index) => (
									<div
										className="user-rates"
										key={`${rate.id ?? "rate"}-${index}`}
									>
										<div className="user-rate">
											<div className="user-rate-header">
												<span className="user-name">
													{rate.fullName?.length > 15
														? rate.fullName.substring(0, 15) + "..."
														: rate.fullName || "Аноним"}
												</span>

												<div className="user-stars">
													{[1, 2, 3, 4, 5].map((star) => (
														<img
															key={star}
															src={rate.rate >= star ? starY : starW}
															alt="star"
															className="star"
														/>
													))}
												</div>
											</div>

											<p className="user-comment">
												{rate.review || "Комментарий отсутствует."}
											</p>

											<p className="user-cdate">
												{formatDate(rate.cDate) || "Комментарий отсутствует."}
											</p>
										</div>
									</div>
								))}

								{reviewsPage < totalReviewsPages && (
									<div ref={loaderRef} className="reviews-scroll-loader">
										<div className="spinner"></div>
										<p>Загрузка...</p>
									</div>
								)}

								{reviewsPage >= totalReviewsPages && allRates.length > 0 && (
									<div className="reviews-end"></div>
								)}
							</>
						) : reviewsLoading ? (
							<div className="reviews-scroll-loader">
								<div className="spinner"></div>
								<p>Загрузка отзывов...</p>
							</div>
						) : (
							<p className="no-reviews">
								Будьте первым, кто оставит отзыв!
								<br />
								Отзыв можно оставить купив товар на сумму 1000 ₽ или больше.
							</p>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Item;
